import * as React from 'react';
import * as ToastPrimitive from '@radix-ui/react-toast';
import { cn } from '@/lib/cn.js';
import { X } from 'lucide-react';

const ToastCtx = React.createContext({ toast: () => {} });

export function ToastProvider({ children }) {
  const [items, setItems] = React.useState([]);
  const toast = React.useCallback(({ title, description, variant = 'default' }) => {
    const id = Math.random().toString(36).slice(2);
    setItems((prev) => [...prev, { id, title, description, variant }]);
  }, []);
  const dismiss = (id) => setItems((prev) => prev.filter((t) => t.id !== id));

  return (
    <ToastCtx.Provider value={{ toast }}>
      <ToastPrimitive.Provider swipeDirection="right">
        {children}
        {items.map((t) => (
          <ToastPrimitive.Root
            key={t.id}
            duration={4000}
            onOpenChange={(open) => !open && dismiss(t.id)}
            className={cn(
              'data-[state=open]:animate-in data-[state=closed]:animate-out',
              'rounded-lg border p-4 shadow-lg flex items-start gap-3 bg-white dark:bg-slate-900',
              t.variant === 'success' && 'border-emerald-300',
              t.variant === 'error' && 'border-red-300',
              t.variant === 'default' && 'border-slate-200 dark:border-slate-700',
            )}
          >
            <div className="flex-1">
              {t.title && (
                <ToastPrimitive.Title className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                  {t.title}
                </ToastPrimitive.Title>
              )}
              {t.description && (
                <ToastPrimitive.Description className="text-sm text-slate-600 dark:text-slate-400">
                  {t.description}
                </ToastPrimitive.Description>
              )}
            </div>
            <ToastPrimitive.Close className="text-slate-400 hover:text-slate-700">
              <X className="h-4 w-4" />
            </ToastPrimitive.Close>
          </ToastPrimitive.Root>
        ))}
        <ToastPrimitive.Viewport className="fixed top-4 right-4 z-[100] flex w-96 max-w-full flex-col gap-2 outline-none" />
      </ToastPrimitive.Provider>
    </ToastCtx.Provider>
  );
}

export function useToast() {
  return React.useContext(ToastCtx);
}
